/**
 * Story 3.3 (FR-44/FR-9, AD-2 client-side resolution) — the frontend mirror
 * of Planigo's `src/support/display-options/effective-resolution.ts`
 * read-side funnel. Every consumer that needs a placed item's
 * option-derived dimensions, flags, or image reads it through THIS module
 * — no consumer reads a placement's product dims / an option's flags /
 * a product's base image url directly.
 *
 * PORTED from the backend module — single source of truth for the
 * orientation permutation table lives there (`effective-resolution.ts`);
 * keep this file's table in sync if it ever changes. Pure, synchronous, no
 * Redux/React — engines-grade purity (NFR-6).
 */
import type { CanonicalDisplayOption } from './contract.types'
import type { CanonicalPlacement } from './simulation.types'
import { toCm } from './units'

export type BaseDims = { width: number; height: number; depth: number }
export type DerivedDims = { width: number; height: number; depth: number }
export type OrientationKey = 1 | 2 | 3 | 4 | 5 | 6

/**
 * 2026-09-09: `resolveEffectiveOption`/`effectiveDims` only ever read
 * `.displayOptionId` off the placement they're given — widened from the
 * full `CanonicalPlacement` to this single-field shape so callers that
 * only HAVE a displayOptionId in hand (no full placement object — e.g. the
 * pegboard engines resolving a hypothetical/candidate move) can call them
 * without fabricating a fake placement or an unsafe cast. Purely a type
 * widening: every existing caller already passes a full `CanonicalPlacement`,
 * which satisfies this narrower shape for free.
 */
export type DisplayOptionIdCarrier = { displayOptionId: string | null | undefined }

/** id (uuid) → option, scoped to a single product's catalog. */
export type OptionLookup = Record<string, CanonicalDisplayOption>

export type EffectiveFlags = {
  isStackable: boolean
  maxStackableCount: number
  isHangable: boolean
  hangHoleX: number | null
  hangHoleY: number | null
}

export type ImageTransform = 'none' | 'rotate90'

export type ImageResult = {
  imageUrl: string
  transform: ImageTransform
  hasDedicatedImage: boolean
  isDimmed: boolean
  /** Review fix (story 4.10) — the uuid of the option `effectiveImage`
   *  ACTUALLY resolved (may differ from `placement.displayOptionId`: a
   *  missing/empty id falls back to the product's default option, per
   *  story 4.8/4.9's convention). `null` when no option resolved.
   *  Consumers building a per-option `<defs>` key MUST use this field,
   *  never `placement.displayOptionId` directly — using the raw
   *  placement field produces a dangling `#opt-null` reference whenever
   *  the resolved option came from the default-option fallback. */
  optionUuid: string | null
}

/**
 * The 6 orientation-dims permutations (T-11/T-16 canonical table). Base
 * product dims are W(idth) x H(eight) x D(epth); each orientation reshuffles
 * which base axis is presented as width/height/depth. Codes 2/4/6 are the
 * 90-degree-tipped variants of 1/3/5. Verified verbatim against
 * `Planigo/src/support/display-options/effective-resolution.ts`.
 *
 *   code | orientation    | width<- | height<- | depth<-
 *    1   | Front          |   W     |    H     |    D
 *    2   | Front Laydown  |   H     |    W     |    D
 *    3   | Side           |   D     |    H     |    W
 *    4   | Side Laydown   |   H     |    D     |    W
 *    5   | Top            |   W     |    D     |    H
 *    6   | Top Rotated    |   D     |    W     |    H
 */
type Axis = 'width' | 'height' | 'depth'
const ORIENTATION_PERMUTATIONS: Record<
  OrientationKey,
  { width: Axis; height: Axis; depth: Axis }
> = {
  1: { width: 'width', height: 'height', depth: 'depth' },
  2: { width: 'height', height: 'width', depth: 'depth' },
  3: { width: 'depth', height: 'height', depth: 'width' },
  4: { width: 'height', height: 'depth', depth: 'width' },
  5: { width: 'width', height: 'depth', depth: 'height' },
  6: { width: 'depth', height: 'width', depth: 'height' },
}

/**
 * Compute the effective dimensions of an orientation from the product's
 * base dims. Pure geometry — the single source of truth for the 6
 * permutations on the frontend. Throws on an out-of-range orientationKey
 * (matching the backend's stance: corrupt data must surface loudly) — the
 * client only ever calls this with a server-supplied key.
 */
export const deriveOrientationDims = (
  baseDims: BaseDims,
  orientationKey: OrientationKey
): DerivedDims => {
  const permutation = ORIENTATION_PERMUTATIONS[orientationKey]
  if (!permutation) {
    throw new Error(
      `deriveOrientationDims: invalid orientationKey (${orientationKey}) — expected 1-6`
    )
  }
  return {
    width: baseDims[permutation.width],
    height: baseDims[permutation.height],
    depth: baseDims[permutation.depth],
  }
}

/**
 * Story 5.5 (AC3): resolve a placement's effective option, defensively
 * falling back to Front (orientationKey=1) when `displayOptionId` is
 * missing. Not expected after the stage-0 migration — this is a
 * frontend-only read guard for a pathological, not-migrated placement
 * (built before displayOptionId existed, when Front was the only/implicit
 * orientation); it never touches the backend or the stored data. A
 * `displayOptionId` that IS set but doesn't resolve (stale/deleted option)
 * is a different case and is left as "not found" for callers to handle as
 * before.
 *
 * Code review 2026-08-18 (decision: user): the fallback deliberately
 * targets Front specifically, NOT "whatever the product's isDefault option
 * currently is" — an operator can reassign a product's default to any
 * option (`OptionDetail.tsx`'s "Set as default"), and a not-migrated
 * placement's geometry reflects the OLD legacy model it was actually built
 * under, not whatever config exists today. Falling back to a since-
 * reassigned default (e.g. a laydown/tray with different dims) would feed
 * the wrong footprint into collision/capacity math.
 *
 * fix-026 (2026-10-19): that is no longer qualified by "only when no Front
 * option exists at all". It used to fall back FURTHER, to the catalog's
 * isDefault option, so the guard resolved "something rather than nothing" —
 * and that tail did the exact harm the paragraph above warns about. A
 * product whose Front orientation is DISABLED has no Front row in its list
 * (the picker filters disabled options, FR-4), so an arranged placement —
 * which carries no displayOptionId precisely because no Front row was found
 * — resolved to the default instead. Live example (product 1134335,
 * 2026-10-19): base dims 40x10, default Front Laydown 10x40, so the AI
 * reserved 40cm slots and the canvas drew 10cm-wide, 40cm-tall units that
 * overflowed a 35cm shelf.
 *
 * Resolving NOTHING is the correct outcome: every caller already falls back
 * to the product's own base dims and base image, which IS Front's geometry —
 * what the guard says a missing id means, and what an ARRANGE packs by.
 *
 * Story 3.3 review follow-up: `''` is treated the same as null/undefined
 * here. `getDefaultOptionId` returns `''` (not null) when a product's
 * catalog is empty — e.g. a just-added product (`AddProductModal`,
 * Excel import) dropped/pasted onto the canvas before the next simulate
 * enriches it. Without this, that `''` would resolve as "stale/deleted"
 * (neutral flags, dimmed base image) instead of "missing" — and stay
 * that way even after the catalog does load, since `''` never matches a
 * real uuid. Folding it into the missing-id branch means it upgrades to
 * the real Front/default option's dims/flags/image the moment the catalog
 * arrives, same as a true null/undefined id already does.
 */
const isMissingOptionId = (placement: DisplayOptionIdCarrier): boolean =>
  placement.displayOptionId == null || placement.displayOptionId === ''

export const resolveEffectiveOption = (
  placement: DisplayOptionIdCarrier,
  optionLookup: OptionLookup
): CanonicalDisplayOption | undefined => {
  const direct = placement.displayOptionId
    ? optionLookup[placement.displayOptionId]
    : undefined
  if (direct) return direct
  if (isMissingOptionId(placement)) {
    const options = Object.values(optionLookup)
    return options.find(
      (option) => option.type === 'orientation' && option.orientationKey === 1
    )
  }
  return undefined
}

/**
 * fix-086 (QA, 2026-10-29) — a display mode's stored dims in CANONICAL CM.
 *
 * Every length in the client model is centimetres: `from-contract.ts`'s
 * `toDimensions` converts the product's own `w/h/d` once into
 * `wCm/hCm/dCm`, and every caller of the readers below hands them a
 * `baseDims` built from those. A display mode is the ONE option type that
 * carries dimensions of its own, and the v3 load passes them straight
 * through, uom and all (`from-contract.ts`'s `displayOptions` map) — so
 * the mode's own Unit of Measure picker is a real unit and not a label: a
 * 1 m tray and a 100 cm tray are the same physical box and must reach the
 * canvas as the same number.
 *
 * Reported 2026-10-29: a product whose base dims are in centimetres,
 * switched to a display mode saved in meters, kept the mode's raw
 * numbers — so a 1 m box drew 1 cm wide and every planner downstream
 * (push, overlap, stack, capacity, NOW) agreed with that wrong picture.
 * The backend twin never had this bug: its `effectiveDims` returns the
 * uom ALONGSIDE the numbers (`EffectiveDims.uom`,
 * `Planigo/src/support/display-options/effective-resolution.ts`); this
 * port dropped that field and kept the numbers as though they were cm.
 *
 * Converted on the READ side, exactly where `optionSeededHangHoleCm`
 * already converted the footprint it centres a hole on (fix-084): the
 * stored row is never rewritten, so the Display Options card keeps
 * showing what was typed in the unit it was typed in, and a save still
 * round-trips byte-stable.
 */
const displayModeDimsCm = (
  option: CanonicalDisplayOption
): DerivedDims | null =>
  option.dims
    ? {
        width: toCm(option.dims.w, option.dims.uom),
        height: toCm(option.dims.h, option.dims.uom),
        depth: toCm(option.dims.d, option.dims.uom),
      }
    : null

/**
 * Effective dims for a placement's option, in CM: orientation options
 * derive from the product's live base dims (already canonical cm, so
 * nothing to convert); display-mode options carry their OWN stored dims
 * (a case/tray/basket has physical dimensions unrelated to the base
 * product) in their OWN uom, converted here (fix-086). Returns null when
 * the option or its dims cannot be resolved — callers fall back to the
 * placement's base dims in that case.
 *
 * `baseDims` MUST be centimetres (`product.dims.wCm` and friends), which
 * is what every caller passes and what every consumer of the result
 * assumes — the canvas scene, the planners and the report painters are
 * all cm throughout.
 */
export const effectiveDims = (
  placement: DisplayOptionIdCarrier,
  optionLookup: OptionLookup,
  baseDims: BaseDims
): DerivedDims | null => {
  const option = resolveEffectiveOption(placement, optionLookup)
  if (!option) return null
  if (option.type === 'display_mode') {
    return displayModeDimsCm(option)
  }
  if (option.orientationKey === null) return null
  try {
    return deriveOrientationDims(
      baseDims,
      option.orientationKey as OrientationKey
    )
  } catch {
    // Story 3.3 review follow-up: deriveOrientationDims intentionally
    // throws on a corrupt/out-of-range orientationKey (matches the
    // backend's stance, asserted by its own test). But `effectiveDims`
    // is called per-placement from `selectEffectiveDimsByPlacement`, a
    // SINGLE memoized selector shared across the whole model — one bad
    // option would otherwise crash dims resolution for every placement,
    // not just the offending one. Degrade to null (this function's own
    // "cannot be resolved" contract) instead of propagating, same as
    // every other unresolvable case above.
    return null
  }
}

/**
 * The full effective dims (not just width) of a single OPTION — same split
 * as `optionEffectiveWidth` just below, for a caller that needs the shape
 * (width AND height) rather than one axis, e.g. the display-options
 * flyout sizing its per-row thumbnail to the option's real aspect ratio
 * instead of a fixed square. Returns null under the same conditions
 * `effectiveDims` does (missing dims, unset orientationKey), and in the
 * same unit — cm, a display mode's own uom converted (fix-086).
 */
export const effectiveDimsForOption = (
  option: CanonicalDisplayOption | undefined,
  baseDims: BaseDims
): DerivedDims | null => {
  if (!option) return null
  if (option.type === 'display_mode') {
    return displayModeDimsCm(option)
  }
  if (option.orientationKey === null) return null
  try {
    return deriveOrientationDims(baseDims, option.orientationKey as OrientationKey)
  } catch {
    return null
  }
}

/**
 * fix-074 (QA, 2026-10-27) — is this option's own configuration too
 * incomplete to place? True only for a DISPLAY MODE that carries no
 * usable footprint of its own: Width, Height, Depth or UOM left blank,
 * or a dimension saved as 0.
 *
 * Orientations can never be incomplete — their dims are DERIVED from the
 * product's live base dims (`deriveOrientationDims`), never stored — so
 * they always return false.
 *
 * Why 0 counts as missing: the 2026-10-19 ruling (fix-019's
 * `changes/2026-10-19-allow-zero-and-blank.md`) deliberately made every
 * numeric field on "Add display mode" optional and allowed 0, leaving
 * open — in that record's own words — "whether the canvas treats a 0 dim
 * like a blank one". It does not: a 0-width mode yields a zero-wide
 * facing, which is not a placement a planner can see or use. Blank and 0
 * are therefore one condition here.
 *
 * Why UOM is part of it: a mode's dims only ever reach the canvas as a
 * SET. The v3 load drops the whole `dims` object unless width, height,
 * depth AND uom are all present (`product-canonical-mapper-sup.ts`'s
 * `toDisplayOptions`), so a mode with three good numbers and no unit
 * arrives exactly as unusable as one with no numbers at all.
 *
 * READ-side gate only — nothing about what may be SAVED changes (the
 * 2026-10-19 ruling stands). It is what the pickers consult before
 * offering an option, so a mode that cannot be placed is never presented
 * as though it could be.
 */
export const isOptionConfigIncomplete = (
  option: CanonicalDisplayOption | undefined
): boolean => {
  if (!option || option.type !== 'display_mode') return false
  const dims = option.dims
  if (!dims || !dims.uom) return true
  return ![dims.w, dims.h, dims.d].every(
    (value) => typeof value === 'number' && Number.isFinite(value) && value > 0
  )
}

/**
 * The effective width of a single OPTION (not a placement) — the same
 * orientation/display-mode split as `effectiveDims`, decoupled from a
 * placement so callers with only an option in hand (e.g. the NOW
 * selector's default-option denominator, story 4.8/FR-31) don't need a
 * fake placement to reuse the branch logic. Returns null under the same
 * conditions `effectiveDims` does (missing dims, unset orientationKey),
 * and in the same unit as the `baseDims` it is given — cm for every
 * caller but `withDefaultOptionWidths`, which converts both ends itself
 * (fix-086).
 */
export const optionEffectiveWidth = (
  option: CanonicalDisplayOption | undefined,
  baseDims: BaseDims
): number | null => {
  if (!option) return null
  if (option.type === 'display_mode') {
    return displayModeDimsCm(option)?.width ?? null
  }
  if (option.orientationKey === null) return null
  try {
    return deriveOrientationDims(baseDims, option.orientationKey as OrientationKey)
      .width
  } catch {
    // Review fix (2026-08-17) — mirrors effectiveDims's own guard (Story
    // 3.3 review follow-up): deriveOrientationDims intentionally throws
    // on a corrupt/out-of-range orientationKey, but this function now
    // feeds a memoized selector shared across every product in the data
    // panel (selectFacingsByProduct) plus the REC calculate-payload
    // builder (withDefaultOptionWidths) — one bad option must not crash
    // NOW/REC for everything else. Degrade to null (this function's own
    // "cannot be resolved" contract), same as every other unresolvable
    // case above.
    return null
  }
}

/**
 * The width ONE placement actually occupies, in `baseDims`' unit — the
 * resolution ladder `selectFacingsByProduct` established (story 4.8 +
 * its 2026-08-17 review fix), extracted so other footprint consumers
 * cannot re-derive it slightly differently:
 *
 *   1. option resolves            -> its effective width (base width if
 *                                    the option itself cannot be sized)
 *   2. `displayOptionId` missing  -> the DEFAULT option's effective width,
 *      or empty                     matching `resolveEffectiveOption`'s
 *                                    convention for a freshly added or
 *                                    pasted placement not yet enriched
 *   3. id set but unresolvable    -> base width ("not found" stays "not
 *      (stale/deleted option)        found")
 *
 * Added 2026-10-14 for `selectPlacedShares` (the hierarchy tabs' Now
 * column), which multiplied `product.dims.wCm` by `placement.facings` and
 * so reported a brand/category footprint share as though every item were
 * in its base orientation. `selectFacingsByProduct` still carries its own
 * inline copy of this ladder — it is covered by its own tests and was left
 * alone deliberately; consolidating the two onto this function is a safe
 * follow-up, not part of a bug fix.
 */
export const placementFacingWidth = (
  options: CanonicalDisplayOption[] | undefined,
  displayOptionId: string | null | undefined,
  baseDims: BaseDims
): number => {
  const all = options ?? []
  const option = all.find((candidate) => candidate.uuid === displayOptionId)
  if (option) return optionEffectiveWidth(option, baseDims) ?? baseDims.width
  if (displayOptionId == null || displayOptionId === '') {
    const defaultOption = all.find((candidate) => candidate.isDefault)
    return optionEffectiveWidth(defaultOption, baseDims) || baseDims.width
  }
  return baseDims.width
}

/**
 * Story 4.9 (AC1, FR-30/32/33) — capacity for a SINGLE placement, the ONE
 * function BOTH the data panel (this story) and the v3 export engine
 * (`export/excel/excel-fieldwise.ts`, story 5.3) must call — client-side
 * only, per spec §6: "no backend capacity computation exists." Called at
 * render time, never persisted (AC5) — `simulation_product_snapshot`
 * carries no capacity column.
 *
 * Formula: `depthLayers = floor(shelfDepth / effective depth)`,
 * `stackLayers = 1 + max(0, stackCount)`, `packs = facings x stackLayers`
 * — the number of physical packs/trays/cases actually PLACED, matching
 * what the canvas draws (facings across the shelf, stacked vertically;
 * depth is never rendered as separate boxes). A display-mode option with
 * a pack `capacity` returns that `packs` figure plus
 * `units = packs x depthLayers x capacity` — the full depth-aware
 * unit-equivalent, including backstock the canvas doesn't render (may be
 * fractional if capacity isn't a whole-dividing number upstream; callers
 * round for display). An orientation (no `capacity` on the option) has no
 * separate pack concept, so it returns `packs x depthLayers` itself as
 * `units` only.
 *
 * [fix-012, 2026-10-19: `packs` no longer multiplies by `depthLayers`.
 * QA-reported defect: a single-facing Case/Tray placement on a shelf
 * shallow enough to fit 2 deep front-to-back reported "2 packs (24
 * units)" when only 1 pack was visible on the canvas — mathematically
 * exactly what the ORIGINAL spec required (SPEC-display-options.md §6,
 * GWT #4: "2 facings x 2 layers x 3 deep -> 12 packages"), but QA/product
 * found it confusing: `packs` mixed "what's actually placed" with
 * "theoretical depth capacity" in one number that never matches the
 * canvas. Product decision, formally amending FR-32/story 4.9 AC2 (see
 * `planning-artifacts/feat-004-display-options/changes/2026-10-19-
 * capacity-packs-exclude-depth-layering.md`): `packs` now tracks only
 * what's placed (facings x stackLayers); `units` is UNCHANGED — the
 * depth-aware unit-equivalent total was never in dispute, only what the
 * `packs` figure means.]
 *
 * [Bug fix 2026-10-08: the original implementation had this inverted —
 * it called `packSlots` itself "units" and then divided by `capacity` to
 * get "packs", shrinking the pack count and leaving the unit-equivalent
 * un-multiplied. A capacity-2 Tray with 10 physical pack-slots reported
 * "5 packs (10 units)" instead of the correct "10 packs (20 units)" —
 * confirmed against the spec's own worked example (SPEC-display-
 * options.md §6: capacity 16 at 2×2×3 → "12 packs and 192 unit-
 * equivalents", i.e. units = packs × capacity, not packs = units /
 * capacity).]
 *
 * [Deviation from the story's literal `option.dims.d` sketch: an
 * orientation option carries no `dims` of its own — only display-mode
 * options store their own physical dims. An orientation's depth is
 * DERIVED from the product's base dims via the same permutation table
 * `optionEffectiveWidth` already uses for width, so `baseDims` is
 * threaded through here for the same reason it is there.]
 *
 * [Deviation from AC6's literal "no-stack (stackCount = 1)": the
 * codebase's own established `stackCount` convention is "additional
 * layers ON TOP of the base one" — `layers = 1 + max(0, stackCount)`
 * (`du-edit.ts`, `ClipboardPanel.tsx`, `stack-trim.ts`'s own doc
 * comment) — so `stackCount = 0` is the no-stack case, not 1. This
 * function reads `placement.stackCount` through that SAME conversion
 * rather than multiplying by the raw field, so a freshly-placed,
 * never-stacked item (`stackCount: 0`) still packs at 1 layer instead
 * of collapsing to zero capacity.]
 */
export const calculateCapacity = (
  placement: { facings: number; stackCount: number },
  option: CanonicalDisplayOption | undefined,
  baseDims: BaseDims,
  shelfDepth: number
): { packs?: number; units: number } => {
  const orientationDepth = (orientationKey: OrientationKey): number => {
    try {
      return deriveOrientationDims(baseDims, orientationKey).depth
    } catch {
      // Review fix (2026-08-17) — mirrors effectiveDims/optionEffectiveWidth's
      // own guard: deriveOrientationDims intentionally throws on a corrupt/
      // out-of-range orientationKey, but this function feeds a memoized
      // selector shared across every product in the data panel
      // (selectCapacityByProduct) — one bad option must not crash capacity
      // computation for everything else. Degrade to the base depth, same as
      // every other unresolvable-option case below.
      return baseDims.depth
    }
  }
  // fix-086: `shelfDepth` is the field's `dims.dCm`, so the mode's own
  // depth has to be cm too — a mode saved in meters divided a cm shelf
  // depth by its raw number and reported ~100x the layers that fit.
  const depth =
    option?.type === 'display_mode'
      ? displayModeDimsCm(option)?.depth ?? baseDims.depth
      : option?.orientationKey != null
        ? orientationDepth(option.orientationKey as OrientationKey)
        : baseDims.depth
  const depthLayers = depth > 0 ? Math.floor(shelfDepth / depth) : 0
  const stackLayers = 1 + Math.max(0, placement.stackCount)
  const packs = placement.facings * stackLayers
  if (option?.type === 'display_mode' && option.capacity && option.capacity > 0) {
    return { packs, units: packs * depthLayers * option.capacity }
  }
  return { units: packs * depthLayers }
}

/**
 * The effective hang point (cm, board-local hole offset convention — see
 * `peg-snap.ts`'s `hungAnchorOffsets`) for a placement's option.
 *
 * Reported 2026-09-21: the Display Options tab's own Horizontal/Vertical
 * fields (`option.flags.hangHoleX/Y`, saved via `OptionDetail.tsx`) always
 * reached the store but had exactly one reader anywhere in the codebase —
 * `effectiveFlags`'s `isHangable` presence check — never as a coordinate.
 * The hang point actually driving geometry was unconditionally the
 * PRODUCT-level `product.hangingPoint` (Product Information tab's own
 * Horizontal/Vertical), so editing an option's hole never moved anything:
 * every option a product had hung at the same single point.
 *
 * User decision (2026-09-21): per-option override. An option whose OWN
 * `hangHoleX`/`hangHoleY` are BOTH set wins; a partial pair (one axis set,
 * the other still null) is not a usable coordinate, so it falls through to
 * `baseHangingPoint` (`product.hangingPoint`) exactly like an option with
 * neither set — same "cannot mix axes from two sources" reasoning
 * `effectiveDims` already applies to width/height/depth as a unit.
 *
 * Units: `product.hangingPoint` is stored in CM (`ProductDetailsModal.tsx`/
 * `addnew.js` both convert through `toCm` before dispatch) and every
 * consumer here (`peg-snap.ts`) treats it as cm with no conversion. The
 * option-level fields have no uom control of their own — entered as plain
 * numbers, saved as-is (`display-option.service.ts`, no transform) — so
 * they are treated the same way: cm, straight through.
 *
 * fix-084 (QA, 2026-10-28): "the hang point is incorrectly positioned in
 * all orientations except the front orientation". A hung product switched
 * to 2/3/4/5/6 hung by its TOP-LEFT CORNER while the Display Options card
 * showed that orientation carrying a hole of its own.
 *
 * Both halves of that come from the SAME stored value — the `(0, 0)` pair
 * story 4.7's `defineAndHang` wrote as "a documented placeholder", which
 * the Display Options card and the Excel import still leave behind
 * (fix-082 closed only the card's own Save). The card never shows it: it
 * SEEDS what it displays (`seedOptionHangHoleCm`, fix-073), and 0 is "not
 * set" under the legacy truthiness every hanging form uses, so it printed
 * the centre of that orientation's width. This resolver took the pair
 * literally — two non-null numbers — and hung the product at `(0, 0)`, the
 * option's own top-left corner. Front escaped because its hole is mirrored
 * from the product's real master-data values (migration 010 /
 * `front-mirror.ts`), never a placeholder: exactly the "all orientations
 * except Front" shape of the report.
 *
 * So the order is now: the option's own stored hole when it IS one; then,
 * for FRONT only, the product's master hole (Front presents the product's
 * own box, so that hole is a real measurement on this footprint and must
 * outrank a guess — fix-045's case, and what keeps the one orientation QA
 * calls correct exactly where it is); then the option's seeded centre,
 * which is what the card shows; then the product's hole, for an option
 * whose footprint does not resolve at all.
 *
 * The 2026-09-21 partial-pair rule is untouched — one axis set and the
 * other null still is not a usable coordinate — it simply falls through to
 * the option's own seeded hole now instead of to a Front-relative value
 * measured on a different box.
 */
export const effectiveHangingPoint = (
  placement: DisplayOptionIdCarrier,
  optionLookup: OptionLookup,
  baseHangingPoint: { h: number; v: number } | undefined,
  baseDimsCm: BaseDims
): { h: number; v: number } | undefined => {
  const option = resolveEffectiveOption(placement, optionLookup)
  const stored = storedOptionHangHole(option)
  if (stored) return stored
  // Front presents the product's own box, so the product's master hole is a
  // real measurement ON THIS footprint and outranks any seeded guess — the
  // one orientation fix-084 reports as already correct must not move.
  if (
    baseHangingPoint &&
    option?.type === 'orientation' &&
    option.orientationKey === 1
  ) {
    return baseHangingPoint
  }
  return optionSeededHangHoleCm(option, baseDimsCm) ?? baseHangingPoint
}

/** The legacy drop below the top edge, in cm (`addnew.js`'s
 *  `defaultObjectLoad`). `hangable-defaults` re-exports it as
 *  `DEFAULT_HANG_VERTICAL` so the seed and this resolver share one literal. */
export const DEFAULT_HANG_VERTICAL_CM = 0.5

/**
 * The hole an option actually STORES, or null — the 2026-09-21 rule
 * (both axes or nothing: one axis set and the other null is not a usable
 * coordinate) narrowed by fix-084 to exclude the `(0, 0)` pair.
 *
 * `(0, 0)` is the option's own top-left corner, which fix-082 ruled is
 * "not a hole anybody means": it is story 4.7's documented placeholder,
 * written by the pre-fix-073 `defineAndHang` and still reachable through
 * the Excel import and `DefineHangingDialog`. Every hanging FORM already
 * reads it as "not set" (legacy truthiness, `seedHangPoint`), so reading
 * it as a coordinate here was the one place the product disagreed with
 * itself.
 */
export const storedOptionHangHole = (
  option: CanonicalDisplayOption | undefined
): { h: number; v: number } | null => {
  const x = option?.flags.hangHoleX ?? null
  const y = option?.flags.hangHoleY ?? null
  return x != null && y != null && !(x === 0 && y === 0) ? { h: x, v: y } : null
}

/** Legacy truthiness, `addnew.js`'s: 0, null, undefined and NaN are all "not
 *  set". A 0 is the box's own top-left corner — "not a hole anybody means"
 *  (fix-082), which is why a stored one is seeded over rather than used. */
const setOrNull = (value: number | null | undefined): number | null =>
  value == null || !Number.isFinite(value) || value === 0 ? null : value

/**
 * fix-084 (QA, 2026-10-28) — the default hole for an option that stores
 * none: the centre of THAT option's own width, `DEFAULT_HANG_VERTICAL_CM`
 * down. Byte-for-byte the rule the Display Options card already SHOWS
 * (`seedOptionHangHoleCm`, fix-073) and that "Define and hang" WRITES, so
 * the canvas can no longer draw a hole somewhere the card does not.
 *
 * A display mode's dims carry their own uom; an orientation's are derived
 * from `baseDimsCm` and are already canonical — the same split
 * `seedOptionHangHoleCm` makes, for the same reason (the option-level hole
 * fields are read as cm by every consumer, see `effectiveHangingPoint`).
 * That split now lives one level down, inside `effectiveDimsForOption`
 * itself (fix-086), so the footprint arrives here already in cm and is
 * halved as-is — converting it a second time here would divide a meters
 * mode by 100 twice over.
 *
 * `null` when there is no footprint to centre on — a display mode with no
 * dims, a corrupt orientation key, or a zero-sized box (fix-074's
 * "incomplete configuration"). The caller then falls back to the product's
 * own point rather than inventing one from nothing.
 */
export const optionSeededHangHoleCm = (
  option: CanonicalDisplayOption | undefined,
  baseDimsCm: BaseDims
): { h: number; v: number } | null => {
  const dims = effectiveDimsForOption(option, baseDimsCm)
  if (!dims || !option) return null
  const { width, height } = dims
  if (setOrNull(width) === null || setOrNull(height) === null) return null
  return { h: width / 2, v: DEFAULT_HANG_VERTICAL_CM }
}

/**
 * The hole an option carries on its OWN footprint — its stored pair when
 * that pair is a hole (legacy truthiness: 0 and blank are "not set"), else
 * the seeded centre above. This is what the Display Options card and
 * "Define and hang" read; `effectiveHangingPoint` composes the same two
 * parts differently, because it also has the product's master hole to
 * defer to and the 2026-09-21 partial-pair rule to honour.
 */
export const optionOwnHangHoleCm = (
  option: CanonicalDisplayOption | undefined,
  baseDimsCm: BaseDims
): { h: number; v: number } | null => {
  if (!option) return null
  const storedH = setOrNull(option.flags.hangHoleX)
  const storedV = setOrNull(option.flags.hangHoleY)
  if (storedH !== null || storedV !== null) {
    return { h: storedH ?? 0, v: storedV ?? 0 }
  }
  return optionSeededHangHoleCm(option, baseDimsCm)
}

/**
 * fix-045 (reported 2026-10-22): the client half of the backend's
 * product → Front mirror (`Planigo/src/support/display-options/front-mirror.ts`,
 * `mirrorProductOntoFront`). Saving the product record rewrites its FRONT
 * orientation row in the same transaction, but the save response carries no
 * options — so the in-memory Front kept its pre-edit hole while
 * `product.hangingPoint` moved on. A shelf→peg drop of a Front placement
 * resolves Front's OWN hole first (`effectiveHangingPoint`), so it hung on
 * the stale one: Product Information said (4, 0), Front still held (0, 0),
 * and every facing hung by its top-left corner.
 *
 * Same rules as the server, so the result is what a reload would load:
 * Front only, not hangable ⇒ null holes, not stackable ⇒ max stack 0, and
 * the holes are the product's raw values (the server copies
 * `haggingHorizontal/Vertical` straight across). Returns null when there is
 * no Front row or nothing changed, so the caller dispatches nothing.
 */
export const mirrorProductOntoFrontOption = (
  options: CanonicalDisplayOption[],
  product: {
    hangable: boolean
    holeX: number
    holeY: number
    stackable: boolean
    maxStack: number
  }
): CanonicalDisplayOption[] | null => {
  const index = options.findIndex(
    (option) => option.type === 'orientation' && option.orientationKey === 1
  )
  if (index < 0) return null
  const front = options[index]
  const flags = {
    ...front.flags,
    isHangable: product.hangable,
    hangHoleX: product.hangable ? product.holeX : null,
    hangHoleY: product.hangable ? product.holeY : null,
    isStackable: product.stackable,
    maxStackableCount: product.stackable ? product.maxStack : 0,
  }
  const unchanged =
    front.flags.isHangable === flags.isHangable &&
    (front.flags.hangHoleX ?? null) === flags.hangHoleX &&
    (front.flags.hangHoleY ?? null) === flags.hangHoleY &&
    front.flags.isStackable === flags.isStackable &&
    (front.flags.maxStackableCount ?? 0) === flags.maxStackableCount
  if (unchanged) return null
  const next = options.slice()
  next[index] = { ...front, flags }
  return next
}

/**
 * fix-075 (QA 2026-10-27): the same product → Front mirror as
 * `mirrorProductOntoFrontOption`, but for the STACKING half alone.
 *
 * The canvas toolbar's "Mark stackable" / "Stackable ON/OFF All" buttons
 * flip the PRODUCT's own `stackable` flag (`OP_SET_STACKABLE` +
 * `/service/product/updateStackable`) and nothing else, so the product and
 * its Front orientation row disagreed the moment either button was pressed:
 * the base product read stackable, Front's own "Is Stackable" stayed off,
 * and `placedStackable` — which needs BOTH (fix-034/fix-069) — kept
 * refusing to stack a product the toolbar had just marked.
 *
 * Only `isStackable`/`maxStackableCount` move. The hanging half is NOT
 * mirrored here: that endpoint writes no hanging column, and a product and
 * its Front row may legitimately disagree on hanging until
 * `011-reconcile-front-mirror.sql` has run (see `catalogUpdated`), so
 * rewriting holes from a stacking toggle would invent a change the server
 * never made.
 *
 * Carries the server's invariant — not stackable ⇒ max stack count 0 — and
 * returns null when there is no Front row or nothing moved.
 */
export const mirrorProductStackingOntoFrontOption = (
  options: CanonicalDisplayOption[],
  product: { stackable: boolean; maxStack: number }
): CanonicalDisplayOption[] | null => {
  const index = options.findIndex(
    (option) => option.type === 'orientation' && option.orientationKey === 1
  )
  if (index < 0) return null
  const front = options[index]
  const flags = {
    ...front.flags,
    isStackable: product.stackable,
    maxStackableCount: product.stackable ? Math.max(0, product.maxStack) : 0,
  }
  const unchanged =
    front.flags.isStackable === flags.isStackable &&
    (front.flags.maxStackableCount ?? 0) === flags.maxStackableCount
  if (unchanged) return null
  const next = options.slice()
  next[index] = { ...front, flags }
  return next
}

/**
 * The stacking + hanging flags for a placement's option — the SINGLE
 * reader (FR-9). Flags live on the option row; archived (disabled) options
 * still return their flags — existing placements keep working. A missing
 * option (pre-migration data, race with a catalog edit) degrades to
 * neutral/non-stacking/non-hanging flags rather than throwing.
 *
 * Story 4.7 (FR-22/23, AC4/AC5/AC6) extends the two rules this function
 * always owned:
 *  - `isHangable` requires BOTH the option's own flag AND a defined hole
 *    (`hangHoleX != null`) — "the option supports hanging" is not enough
 *    on its own; without a hole there is nowhere to hang FROM. This is
 *    the single gate `DefineHangingDialog`'s flows exist to clear (the
 *    "Define and hang" choice sets both via `updateOption`). Applies
 *    identically to orientation AND display-mode options — a display
 *    mode with a hole is exactly as hangable as an orientation with one.
 *  - `isStackable` is forced `false` whenever the placement is CURRENTLY
 *    hung (`placement.loc.type === 'peg'`), regardless of the option's
 *    own stacking flag — hung items never stack (existing product rule,
 *    now enforced at the single-reader level rather than left to each
 *    consumer). `isHanged` (placement-state) vs `isHangable` (option-
 *    property) are deliberately distinct: a shelf placement using a
 *    hangable-and-holed option is still `isStackable` per its flags —
 *    only actually being ON a peg suppresses stacking.
 */
export const effectiveFlags = (
  placement: CanonicalPlacement,
  optionLookup: OptionLookup
): EffectiveFlags => {
  const option = resolveEffectiveOption(placement, optionLookup)
  const isHanged = placement.loc.type === 'peg'
  if (!option) {
    // Deliberately NOT given `effectiveImage`'s missing-id Front fallback
    // (2026-09-08): an image has a defensible default (the product's own
    // bitmap, which IS what Front shows), but stacking and hanging FLAGS live
    // only on an option row — with nothing resolved there is no honest value to
    // report, and inventing `isStackable: true` would let a placement stack or
    // hang on rules nobody has defined. Neutral flags are the truthful
    // "unknown", and the caller that can do better (the peg-drop gate in
    // `useDragController`) resolves through `resolveEffectiveOption` first.
    //
    // Story 5.5 AC3's "treated as Front" is still satisfied wherever a catalog
    // exists: `resolveEffectiveOption` returns the real Front/default option
    // and this branch never runs. An EMPTY catalog is the only gap, and it is
    // closed at the source instead — every v3 response now ships the picker
    // list (`enrichCatalogDisplayOptions`) and the client-minted add-product /
    // Excel-import rows fetch theirs on add.
    return {
      isStackable: false,
      maxStackableCount: 0,
      isHangable: false,
      hangHoleX: null,
      hangHoleY: null,
    }
  }
  const hangHoleX = option.flags.hangHoleX ?? null
  return {
    isStackable: isHanged ? false : option.flags.isStackable,
    maxStackableCount: option.flags.maxStackableCount ?? 0,
    isHangable: option.flags.isHangable && hangHoleX != null,
    hangHoleX,
    hangHoleY: option.flags.hangHoleY ?? null,
  }
}

/**
 * Resolve the image for ONE already-known option — the part of
 * `effectiveImage` below that needs no placement/catalog lookup, pulled out
 * so a caller that already has a specific option in hand (e.g. the display-
 * options flyout, rendering one row per fixed orientation key) can resolve
 * its preview image without fabricating a placement. A dedicated per-option
 * image always wins — never dimmed, never force-rotated. The base image is
 * representative only for Front (as-is) and Front Laydown (rotated 90
 * client-side); orientations 3-6 and display modes fall back to the base
 * image, dimmed (the sole "not representative" signal).
 *
 * The backend's `hasDedicatedImage` flag — NOT the presence of a URL — decides
 * whether a dedicated image is in play, and `imageUrl` must also be non-empty
 * so a flag set without a url can never render a blank image (the same guard
 * Planigo's own `effectiveImage` states in its header).
 *
 * fix-016 (2026-10-15): this used to key off `option.imageUrl` alone, which was
 * wrong against the payload the SIMULATE path actually sends. Planigo resolves
 * the image server-side and then puts the RESULT on the wire —
 * `product-options-read-projection.ts` does `dto.setImageUrl(image.imageUrl)`,
 * and `effective-resolution.ts` returns the BASE image url (with
 * `hasDedicatedImage: false`) for every option that has no photo of its own. So
 * every option arrived carrying a truthy url, this branch claimed all of them
 * were dedicated, and two rules silently died on that path: nothing was ever
 * dimmed (the UX spec's single "not representative" signal), and Front Laydown
 * was never turned 90° (`transform: 'none'`), on the canvas and the clipboard
 * card as much as in the flyout. The ARRANGE path sends no `imageUrl` at all,
 * so it kept applying the real rules — which is how the two screens came to
 * disagree, reported 2026-10-15.
 *
 * The flag was always on the wire and always correct; it was simply not read.
 */
export const effectiveImageForOption = (
  option: CanonicalDisplayOption,
  baseImageUrl: string
): ImageResult => {
  if (option.hasDedicatedImage && option.imageUrl) {
    return {
      imageUrl: option.imageUrl,
      transform: 'none',
      hasDedicatedImage: true,
      isDimmed: false,
      optionUuid: option.uuid,
    }
  }
  const isFront = option.type === 'orientation' && option.orientationKey === 1
  const isFrontLaydown =
    option.type === 'orientation' && option.orientationKey === 2
  if (isFront) {
    return {
      imageUrl: baseImageUrl,
      transform: 'none',
      hasDedicatedImage: false,
      isDimmed: false,
      optionUuid: option.uuid,
    }
  }
  if (isFrontLaydown) {
    return {
      imageUrl: baseImageUrl,
      transform: 'rotate90',
      hasDedicatedImage: false,
      isDimmed: false,
      optionUuid: option.uuid,
    }
  }
  return {
    imageUrl: baseImageUrl,
    transform: 'none',
    hasDedicatedImage: false,
    isDimmed: true,
    optionUuid: option.uuid,
  }
}

/**
 * Resolve the image for a placement's option — the missing-option fallback
 * rules (below), then `effectiveImageForOption` for a resolved one.
 *
 * `baseImageUrl` is the placement's product's own image — this module has
 * no product lookup of its own (pure, no store access), so the caller
 * (a selector, which already has both the placement and its product)
 * passes it in. [Deviation from the story's 2-arg sketch: a 3rd
 * `baseImageUrl` param is required — there is no other source for the
 * Front/Front-Laydown/dimmed fallback image.]
 */
export const effectiveImage = (
  placement: CanonicalPlacement,
  optionLookup: OptionLookup,
  baseImageUrl: string
): ImageResult => {
  const option = resolveEffectiveOption(placement, optionLookup)
  if (!option) {
    // Story 5.5 AC3 (read guard), verbatim: a placement "missing
    // `displayOptionId` … is treated as Front (orientationKey=1 / default
    // option)", and AC3 names the canvas renderer (story 4.10) as one of the
    // three surfaces it governs. `resolveEffectiveOption` can only honour that
    // by FINDING a Front option, so when the product's catalog is EMPTY it
    // resolved nothing and this branch dimmed the product — the opposite of
    // the guard's documented outcome, and a case story 4.10's dimming table
    // (Front · Front Laydown · orientations 3-6 · dedicated image · display
    // mode) has no row for. It also gave dimming a second meaning, against
    // the UX spec's "Dimming: exactly one meaning (non-representative
    // image)". So a missing id now yields Front's OWN documented result —
    // base image, unrotated, undimmed (FR-26/FR-27, story 4.10 row 1).
    //
    // An empty catalog is the ordinary state for a product whose
    // `displayOptions[]` has not arrived: an Excel-imported or
    // add-product-modal row (both hardcode `[]`), or — until the matching
    // backend fix — any catalog product that was not already placed.
    //
    // A `displayOptionId` that IS set but does not resolve (a stale/deleted
    // option) is a DIFFERENT case, outside AC3's wording: there the picture
    // genuinely cannot be vouched for, so it keeps the dimmed fallback.
    if (isMissingOptionId(placement)) {
      return {
        imageUrl: baseImageUrl,
        transform: 'none',
        hasDedicatedImage: false,
        isDimmed: false,
        optionUuid: null,
      }
    }
    return {
      imageUrl: baseImageUrl,
      transform: 'none',
      hasDedicatedImage: false,
      isDimmed: true,
      optionUuid: null,
    }
  }
  return effectiveImageForOption(option, baseImageUrl)
}

/**
 * fix-031 Rule 3 — hangable options **in the order "Switch to a hangable
 * orientation" tries them**: Front first (the upright orientation, and what
 * the planner means by "hang it"), then the remaining orientations by key,
 * then display modes (a deliberate pack, never the obvious answer to "just
 * hang it"). The caller takes the first that FITS, so this is a list, not a
 * pick. `displayOptions` arrives unsorted, so array order must never decide.
 *
 * fix-067: moved here from `useDragController` so the hung product's flyout
 * "Switch" (ProductContextMenu) orders its candidates the same way.
 */
export const orderHangableOptions = (
  candidates: CanonicalDisplayOption[]
): CanonicalDisplayOption[] =>
  [...candidates].sort((a, b) => {
    const rank = (option: CanonicalDisplayOption) =>
      option.type === 'orientation' && option.orientationKey != null
        ? option.orientationKey
        : Number.MAX_SAFE_INTEGER
    return rank(a) - rank(b)
  })

/**
 * The product's default option uuid, or `''` if the catalog is empty
 * (pre-migration product / enrichment failure) — the same "missing
 * displayOptionId → default option" fallback used by stories 3.1/3.2's
 * backend copies. Used by client-side engines that mint a NEW placement
 * without an obvious source to carry `displayOptionId` forward from (e.g.
 * clipboard paste, where the clipboard entry itself doesn't carry one —
 * story 3.5's scope; this is the interim fallback until then).
 */
export const getDefaultOptionId = (
  options: CanonicalDisplayOption[]
): string => {
  const found = options.find((option) => option.isDefault)
  return found ? found.uuid : ''
}
